import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  Paper,
  Typography,
  Grid2 as Grid,
  Chip,
  Stack,
  Button,
  Checkbox,
  Tooltip,
  Alert,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Loader } from "src/shared/components";
import { TablePage } from "src/shared/components/Table";
import { useConfirmModal } from "src/shared/hooks";
import { useTableSearchParams } from "src/shared/hooks/useTableSearchParams";
import { useAuth } from "src/shared/lib/rback/AuthContext";
import {
  useGetAppLvlSettingsQuery,
  useGetServiceLvlSettingsQuery,
  useGetAccountLvlSettingsQuery,
  useUpdateAppLvlSettingsMutation,
  useUpdateServiceLvlSettingsMutation,
  useUpdateAccountLvlSettingsMutation,
  useCheckLaunchedProcessesQuery,
} from "../model/api/api";
import { AnchorLink } from "./components/StyledLink";
import SwitchRow from "./components/SwitchRow";
import {
  services,
  TOOL_NAME_TO_KEY,
  tools,
  serviceTabsOptions,
  EDIT_PERMS,
} from "./const/const";
import {
  ServiceId,
  AppsFormValues,
  ServiceFormValues,
  AccountsFormValues,
  ToolKey,
  AppSvcStatus,
  AccountFieldPath,
  Row,
} from "./types/types";
import {
  isServiceId,
  appSvcStatusToValue,
  appSvcIsLocked,
  appSvcIsDisabled,
  accLvlStatusToValue,
  getErrMsg,
  boolToAppStatus,
  isLocked,
  isDisabled,
} from "./utils/utils";
type Scope = "apps" | "service" | "accounts" | null;

export const ServiceToolsClient = () => {
  const { client_id } = useParams();
  const { hasPermission } = useAuth();
  const readOnly =
    hasPermission("crm:access:servicetools:view") && !hasPermission(EDIT_PERMS);
  const {
    activeTab: activeService,
    setActiveTab: setActiveService,
    onPaginationModelChange,
    paramsObject,
  } = useTableSearchParams<{
    service: ServiceId;
    page?: string;
    per_page?: string;
  }>({
    tabName: "service",
  });

  const currentService: ServiceId = isServiceId(activeService)
    ? activeService
    : services[0].id;

  const page = paramsObject.page ? Number(paramsObject.page) : 1;
  const per_page = paramsObject.per_page ? Number(paramsObject.per_page) : 15;

  const {
    data: appLvlSettingsData,
    isFetching: appLvlSettingsLoading,
    refetch: appLvlSettingsRefetch,
  } = useGetAppLvlSettingsQuery({ client_id });

  const {
    data: serviceLvlSettingsData,
    isLoading: serviceLvlSettingsLoading,
    refetch: serviceLvlSettingsRefetch,
  } = useGetServiceLvlSettingsQuery({ client_id, service: currentService });

  const {
    data: accountLvlSettingsData,
    isFetching: accountLvlSettingsLoading,
    refetch: accountLvlSettingsRefetch,
  } = useGetAccountLvlSettingsQuery({
    client_id,
    service: currentService,
    page,
    per_page,
  });

  const {
    data: checkLaunchedProcessesData,
    isLoading: checkLaunchedProcessesLoading,
  } = useCheckLaunchedProcessesQuery(undefined);

  const isLaunched = !!checkLaunchedProcessesData?.data?.is_launched;

  const [updateAppLvlSettings, { isLoading: isUpdateAppLvlSettings }] =
    useUpdateAppLvlSettingsMutation();
  const [updateServiceLvlSettings, { isLoading: isUpdateServiceLvlSettings }] =
    useUpdateServiceLvlSettingsMutation();
  const [updateAccountLvlSettings, { isLoading: isUpdateAccountLvlSettings }] =
    useUpdateAccountLvlSettingsMutation();

  const {
    handleSubmit: handleSubmitApps,
    reset: resetApps,
    setValue: setAppValue,
    control: controlApps,
    getValues: getAppsValues,
    formState: { dirtyFields: dirtyAppsFields },
  } = useForm<AppsFormValues>({ defaultValues: { apps: {} } });

  const {
    handleSubmit: handleSubmitService,
    reset: resetService,
    setValue: setServiceValue,
    control: controlService,
    getValues: getServiceValues,
    formState: { dirtyFields: dirtyServiceFields },
  } = useForm<ServiceFormValues>({ defaultValues: { service: {} } });

  const {
    handleSubmit: handleSubmitAccounts,
    reset: resetAccounts,
    setValue: setAccountValue,
    control: controlAccounts,
    getValues: getAccountsValues,
    formState: { dirtyFields: dirtyAccountsFields },
  } = useForm<AccountsFormValues>({ defaultValues: { accounts: {} } });

  const appsState = useWatch({ control: controlApps, name: "apps" }) ?? {};
  const svcState = useWatch({ control: controlService, name: "service" }) ?? {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accountsState =
    useWatch({ control: controlAccounts, name: "accounts" }) ?? {};

  const rowToolIdMapRef = useRef<
    Record<number, Partial<Record<ToolKey, number>>>
  >({});
  const rowWarnMapRef = useRef<
    Record<number, Partial<Record<ToolKey, boolean>>>
  >({});
  const rowDisabledMapRef = useRef<
    Record<number, Partial<Record<ToolKey, boolean>>>
  >({});

  const appItems = useMemo(
    () =>
      [...(appLvlSettingsData?.data ?? [])]
        .sort((a, b) => Number(a.service_tool.id) - Number(b.service_tool.id))
        .map(
          (item: {
            id: number | string;
            service_tool: {
              id: number | string;
              displayable_name: string;
            };
            status: AppSvcStatus | null;
          }) => ({
            id: String(item.id),
            label: item.service_tool.displayable_name,
            initial: appSvcStatusToValue(item.status),
            locked: appSvcIsLocked(item.status),
            disabled: appSvcIsDisabled(item.status),
          }),
        ),
    [appLvlSettingsData?.data],
  );

  const appLockedMap = useMemo(
    () =>
      Object.fromEntries(
        appItems.map((i: { id: string; locked: boolean }) => [
          i.id,
          !!i.locked,
        ]),
      ) as Record<string, boolean>,
    [appItems],
  );

  const appDisabledMap = useMemo(
    () =>
      Object.fromEntries(
        appItems.map((i: { id: string; disabled: boolean }) => [
          i.id,
          !!i.disabled,
        ]),
      ) as Record<string, boolean>,
    [appItems],
  );

  const appDisabledMapWithPermissions = useMemo(
    () =>
      Object.fromEntries(
        appItems.map((i) => [i.id, readOnly || !!appDisabledMap[i.id]]),
      ) as Record<string, boolean>,
    [appItems, appDisabledMap, readOnly],
  );

  const serviceItems = useMemo(
    () =>
      (serviceLvlSettingsData?.data ?? []).map(
        (item: {
          id: number | string;
          service_tool: { id: number | string; displayable_name: string };
          status: AppSvcStatus | null;
        }) => ({
          id: String(item.id),
          label: item.service_tool.displayable_name,
          initial: appSvcStatusToValue(item.status),
          locked: appSvcIsLocked(item.status),
          disabled: appSvcIsDisabled(item.status),
        }),
      ),
    [serviceLvlSettingsData?.data],
  );

  const serviceLockedMap = useMemo(
    () =>
      Object.fromEntries(
        serviceItems.map((i: { id: string; locked: boolean }) => [
          i.id,
          !!i.locked,
        ]),
      ) as Record<string, boolean>,
    [serviceItems],
  );

  const serviceDisabledMap = useMemo(
    () =>
      Object.fromEntries(
        serviceItems.map((i: { id: string; disabled: boolean }) => [
          i.id,
          !!i.disabled,
        ]),
      ) as Record<string, boolean>,
    [serviceItems],
  );

  const serviceDisabledMapWithPermissions = useMemo(
    () =>
      Object.fromEntries(
        serviceItems.map((i) => [i.id, readOnly || !!serviceDisabledMap[i.id]]),
      ) as Record<string, boolean>,
    [serviceItems, serviceDisabledMap, readOnly],
  );

  useEffect(() => {
    if (!appItems.length) return;
    const defaults: Record<string, boolean> = {};
    for (const it of appItems) defaults[it.id] = !!it.initial;
    resetApps({ apps: defaults });
  }, [appItems, resetApps]);

  useEffect(() => {
    if (!serviceItems.length) return;
    const defaults: Record<string, boolean> = {};
    for (const it of serviceItems) defaults[it.id] = !!it.initial;
    resetService({ service: defaults });
  }, [serviceItems, resetService]);

  useEffect(() => {
    const src = accountLvlSettingsData?.data ?? [];
    if (!src.length) {
      resetAccounts({ accounts: {} });

      return;
    }
    const defaults: AccountsFormValues["accounts"] = {};
    src.forEach((r) => {
      const row: Partial<Record<ToolKey, boolean>> = {};
      (r.service_tools_settings ?? []).forEach((st) => {
        const key = TOOL_NAME_TO_KEY[st?.service_tool?.name];
        if (!key) return;
        row[key] = accLvlStatusToValue(st?.status);
      });
      defaults[String(r.id)] = row;
    });
    resetAccounts({ accounts: defaults });
  }, [accountLvlSettingsData, resetAccounts]);

  const [lastTouched, setLastTouched] = useState<
    "apps" | "service" | "accounts" | null
  >(null);

  const isAppsDirty = !!Object.keys(dirtyAppsFields.apps ?? {}).length;
  const isServiceDirty = !!Object.keys(dirtyServiceFields.service ?? {}).length;

  const hasDirtyAccounts = (dirty?: AccountsFormValues["accounts"]): boolean =>
    !!dirty &&
    Object.values(dirty).some((row) => !!row && Object.keys(row).length > 0);

  const isAccountsDirty = hasDirtyAccounts(
    dirtyAccountsFields.accounts as unknown as AccountsFormValues["accounts"],
  );

  const showActions = isAppsDirty || isServiceDirty || isAccountsDirty;

  const dirtyScope: Scope = useMemo(() => {
    if (lastTouched === "apps" && isAppsDirty) return "apps";
    if (lastTouched === "service" && isServiceDirty) return "service";
    if (lastTouched === "accounts" && isAccountsDirty) return "accounts";
    if (isAppsDirty) return "apps";
    if (isServiceDirty) return "service";
    if (isAccountsDirty) return "accounts";

    return null;
  }, [lastTouched, isAppsDirty, isServiceDirty, isAccountsDirty]);

  const pendingActionRef = useRef<null | (() => void)>(null);
  const dirtyScopeRef = useRef<Scope>(null);
  useEffect(() => {
    dirtyScopeRef.current = dirtyScope;
  }, [dirtyScope]);

  const resetAppsFromServer = async () => {
    const res = await appLvlSettingsRefetch().unwrap();
    const defaults: Record<string, boolean> = {};
    (res.data ?? []).forEach(
      (it: { id: number; status: AppSvcStatus | null }) => {
        defaults[String(it.id)] = !!appSvcStatusToValue(it.status);
      },
    );
    resetApps({ apps: defaults }, { keepDirty: false, keepTouched: false });
  };

  const resetServiceFromServer = async () => {
    const res = await serviceLvlSettingsRefetch().unwrap();
    const defaults: Record<string, boolean> = {};
    (res.data ?? []).forEach(
      (it: { id: number; status: AppSvcStatus | null }) => {
        defaults[String(it.id)] = !!appSvcStatusToValue(it.status);
      },
    );
    resetService(
      { service: defaults },
      { keepDirty: false, keepTouched: false },
    );
  };

  const resetAccountsFromServer = async () => {
    const res = await accountLvlSettingsRefetch().unwrap();
    const accDefaults: AccountsFormValues["accounts"] = {};
    (res.data ?? []).forEach((r) => {
      const row: Partial<Record<ToolKey, boolean>> = {};
      (r.service_tools_settings ?? []).forEach((st) => {
        const key = TOOL_NAME_TO_KEY[st?.service_tool?.name];
        if (key) row[key] = accLvlStatusToValue(st?.status);
      });
      accDefaults[String(r.id)] = row;
    });
    resetAccounts(
      { accounts: accDefaults },
      { keepDirty: false, keepTouched: false },
    );
  };

  const { modal: crossGuardModal, showModal: showCrossGuardModal } =
    useConfirmModal({
      resolveOnCancel: true,
      resolveOnClose: true,
      width: 560,
      title: "У вас есть несохранённые изменения",
      children: (
        <Typography>
          У вас есть несохранённые изменения в блоке{" "}
          <b>
            {lastTouched === "apps"
              ? "по приложениям"
              : lastTouched === "service"
                ? "по сервисам"
                : "по аккаунтам"}
          </b>
          . Сохранить их?
        </Typography>
      ),
      confirmBtnProps: { children: "Сохранить", isLoading: false },
      cancelBtnProps: { children: "Отмена" },
      onResolve: async ({ result, closeModal }) => {
        const scope = dirtyScopeRef.current;
        try {
          if (result) {
            if (scope === "apps") {
              await handleSubmitApps(onSubmitApps)();
              resetApps(getAppsValues(), { keepDirty: false });
            } else if (scope === "service") {
              await handleSubmitService(onSubmitService)();
              resetService(getServiceValues(), { keepDirty: false });
            } else if (scope === "accounts") {
              await handleSubmitAccounts(onSubmitAccounts)();
            }
          } else {
            try {
              if (scope === "apps") await resetAppsFromServer();
              else if (scope === "service") await resetServiceFromServer();
              else if (scope === "accounts") await resetAccountsFromServer();
            } catch (e) {
              const area =
                scope === "apps"
                  ? "приложений"
                  : scope === "service"
                    ? "сервисов"
                    : "аккаунтов";
              enqueueSnackbar(
                `Не удалось обновить данные ${area}: ${getErrMsg(e)}`,
                { variant: "error" },
              );
            }
          }

          const action = pendingActionRef.current;
          if (action) action();
        } finally {
          pendingActionRef.current = null;
          setLastTouched(null);
          closeModal();
        }
      },
    });

  const openGuard = useCallback(() => {
    showCrossGuardModal();
  }, [showCrossGuardModal]);

  const handleToggleApp = (key: string, checked: boolean) => {
    if (readOnly) return;
    if (dirtyScope && dirtyScope !== "apps") {
      pendingActionRef.current = () => {
        setAppValue(`apps.${key}`, checked, { shouldDirty: true });
        setLastTouched("apps");
      };
      openGuard();

      return;
    }
    setAppValue(`apps.${key}`, checked, { shouldDirty: true });
    setLastTouched("apps");
  };

  const handleToggleService = (key: string, checked: boolean) => {
    if (readOnly) return;
    if (dirtyScope && dirtyScope !== "service") {
      pendingActionRef.current = () => {
        setServiceValue(`service.${key}`, checked, { shouldDirty: true });
        setLastTouched("service");
      };
      openGuard();

      return;
    }
    setServiceValue(`service.${key}`, checked, { shouldDirty: true });
    setLastTouched("service");
  };

  const toggleAccountCell = useCallback(
    (rowId: number, key: ToolKey, next: boolean) => {
      if (readOnly) return;
      const path = `accounts.${String(rowId)}.${key}` as AccountFieldPath;

      if (dirtyScope && dirtyScope !== "accounts") {
        pendingActionRef.current = () => {
          setAccountValue(path, next, { shouldDirty: true });
          setLastTouched("accounts");
        };
        openGuard();

        return;
      }

      setAccountValue(path, next, { shouldDirty: true });
      setLastTouched("accounts");
    },
    [dirtyScope, setAccountValue, openGuard, readOnly],
  );

  const onSubmitApps = async (data: AppsFormValues) => {
    const dirty = (dirtyAppsFields.apps ?? {}) as Record<string, true>;
    const changedIds = Object.keys(dirty);
    if (!changedIds.length) return;

    const payload = changedIds.map((idStr) => ({
      id: Number(idStr),
      status: boolToAppStatus(!!data.apps[idStr]),
    }));

    try {
      await updateAppLvlSettings({
        client_id: String(client_id ?? ""),
        data: payload,
      }).unwrap();
      const vals = getAppsValues();
      resetApps(vals, { keepDirty: false });
      enqueueSnackbar("Настройки приложений сохранены", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(
        `Не удалось сохранить настройки приложений: ${getErrMsg(e)}`,
        {
          variant: "error",
        },
      );
    }
  };

  const onSubmitService = async (data: ServiceFormValues) => {
    const dirty = (dirtyServiceFields.service ?? {}) as Record<string, true>;
    const changedIds = Object.keys(dirty);
    if (!changedIds.length) return;

    const changes = changedIds.map((idStr) => ({
      id: Number(idStr),
      status: data.service[idStr] ? "on" : ("off" as const),
    }));

    try {
      await updateServiceLvlSettings({
        client_id: client_id!,
        service: currentService,
        data: changes,
      }).unwrap();
      const vals = getServiceValues();
      resetService(vals, { keepDirty: false });
      enqueueSnackbar("Настройки сервисов сохранены", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(
        `Не удалось сохранить настройки сервисов: ${getErrMsg(e)}`,
        {
          variant: "error",
        },
      );
    }
  };

  const onSubmitAccounts = async (values: AccountsFormValues) => {
    const dirty =
      (dirtyAccountsFields.accounts as Record<
        string,
        Partial<Record<ToolKey, true>>
      >) || {};
    const changes: Array<{ id: number; status: "checked" | "unchecked" }> = [];

    for (const [rowIdStr, rowMap] of Object.entries(dirty)) {
      const rowId = Number(rowIdStr);
      const idMap = rowToolIdMapRef.current[rowId] || {};
      for (const k of Object.keys(rowMap ?? {}) as ToolKey[]) {
        const settingId = idMap[k];
        if (settingId != null) {
          const isOn = !!values.accounts[rowIdStr]?.[k];
          changes.push({
            id: settingId,
            status: isOn ? "checked" : "unchecked",
          });
        }
      }
    }

    if (!changes.length) return;

    try {
      await updateAccountLvlSettings({
        client_id: client_id!,
        service: currentService,
        data: changes,
      }).unwrap();
      const current = getAccountsValues();
      resetAccounts(current, { keepDirty: false });
      enqueueSnackbar("Настройки аккаунтов сохранены", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(
        `Не удалось сохранить настройки аккаунтов: ${getErrMsg(e)}`,
        {
          variant: "error",
        },
      );
    }
  };

  const handleSave = () => {
    if (lastTouched === "service")
      return handleSubmitService(onSubmitService)();
    if (lastTouched === "accounts")
      return handleSubmitAccounts(onSubmitAccounts)();

    return handleSubmitApps(onSubmitApps)();
  };

  const handleCancel = async () => {
    try {
      const tasks = [];
      if (isAppsDirty) tasks.push(resetAppsFromServer());
      if (isServiceDirty) tasks.push(resetServiceFromServer());
      if (isAccountsDirty) tasks.push(resetAccountsFromServer());
      if (tasks.length) await Promise.all(tasks);
      enqueueSnackbar("Изменения отменены", { variant: "info" });
    } catch (e) {
      enqueueSnackbar(`Не удалось отменить изменения: ${getErrMsg(e)}`, {
        variant: "error",
      });
    } finally {
      setLastTouched(null);
    }
  };

  const withUnsavedGuard = <T extends unknown[]>(fn: (...args: T) => void) => {
    return (...args: T) => {
      if (showActions) {
        pendingActionRef.current = () => fn(...args);
        openGuard();

        return;
      }
      fn(...args);
    };
  };

  const handleGuardedPaginationChange = withUnsavedGuard(
    onPaginationModelChange,
  );

  const handleGuardedTabsChange = withUnsavedGuard((v: unknown) => {
    if (isServiceId(v)) setActiveService!(v);
  });

  const rows = useMemo<Row[]>(() => {
    const src = accountLvlSettingsData?.data ?? [];
    const nextIdMap: Record<number, Partial<Record<ToolKey, number>>> = {};
    const nextWarnMap: Record<number, Partial<Record<ToolKey, boolean>>> = {};
    const nextDisabledMap: Record<
      number,
      Partial<Record<ToolKey, boolean>>
    > = {};

    const result: Row[] = src.map((r) => {
      const base: Row = {
        id: r.id,
        serviceId: currentService,
        accountName: r.account_login,
        accountId: r.account_id,
        contractNumber: r.contract_number,
        contractTag: r.contract_self_rule ? "СУ" : "НВ",

        reco: false,
        advisor: false,
        reportlab: false,
        utm: false,
        flows: false,
        automation: false,
      };

      const idMap: Partial<Record<ToolKey, number>> = {};

      for (const st of r.service_tools_settings ?? []) {
        const key = TOOL_NAME_TO_KEY[st.service_tool?.name];
        if (!key) continue;

        base[key] = accLvlStatusToValue(st.status);
        idMap[key] = st.id;

        (nextWarnMap[r.id] ??= {})[key] = isLocked(st.status);
        (nextDisabledMap[r.id] ??= {})[key] = isDisabled(st.status);
      }

      const override = accountsState[String(r.id)];
      if (override) {
        (Object.keys(override) as ToolKey[]).forEach((k) => {
          const v = override[k];
          if (typeof v === "boolean") base[k] = v;
        });
      }

      nextIdMap[r.id] = idMap;

      return base;
    });

    rowToolIdMapRef.current = nextIdMap;
    rowWarnMapRef.current = nextWarnMap;
    rowDisabledMapRef.current = nextDisabledMap;

    return result;
  }, [accountLvlSettingsData, currentService, accountsState]);

  const columns: GridColDef<Row>[] = useMemo(() => {
    const getHeaderCheckboxProps = (key: ToolKey) => {
      const toggleable = rows.filter(
        (r) =>
          !rowWarnMapRef.current[r.id]?.[key] &&
          !rowDisabledMapRef.current[r.id]?.[key],
      );

      const total = toggleable.length;
      const onCount = toggleable.reduce((acc, r) => acc + (r[key] ? 1 : 0), 0);

      return {
        checked: total > 0 && onCount === total,
        indeterminate: onCount > 0 && onCount < total,
        disabled: total === 0,
        onChange: (
          _: React.ChangeEvent<HTMLInputElement>,
          checked: boolean,
        ) => {
          toggleable.forEach((r) => toggleAccountCell(r.id, key, checked));
        },
      };
    };

    const base: GridColDef<Row>[] = [
      {
        field: "accountName",
        headerName: "Аккаунт",
        flex: 1,
        renderCell: ({ row }) => (
          <Stack>
            <Typography fontWeight={500}>{row.accountName}</Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {row.accountId}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "contractNumber",
        headerName: "Договор",
        flex: 1,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>{row.contractNumber}</Typography>
            {row.contractTag && <Chip size="small" label={row.contractTag} />}
          </Stack>
        ),
      },
    ];

    const toolCols: GridColDef<Row>[] = tools.map(({ key, label }) => ({
      field: key,
      headerName: label,
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const props = getHeaderCheckboxProps(key);

        return (
          <Stack
            sx={{
              width: "100%",
              height: "100%",
            }}
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
          >
            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                whiteSpace: "normal",
                lineHeight: 1.2,
                height: "2.4em",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {label}
            </Typography>
            <Checkbox
              size="small"
              {...props}
              inputProps={{ "aria-label": `Выбрать все: ${label}` }}
            />
          </Stack>
        );
      },
      renderCell: ({ row }) => {
        const locked = !!rowWarnMapRef.current[row.id]?.[key];
        const disabled = !!rowDisabledMapRef.current[row.id]?.[key];

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {locked ? (
              <Tooltip
                placement="top"
                disableInteractive={false}
                slotProps={{
                  tooltip: {
                    sx: (t) => ({
                      bgcolor: t.palette.background.paper,
                      color: t.palette.text.primary,
                      boxShadow: t.shadows[3],
                      border: `1px solid ${t.palette.divider}`,
                      p: 3,
                    }),
                  },
                  arrow: { sx: (t) => ({ color: t.palette.background.paper }) },
                }}
                title={
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Недоступно для управления.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Доступность определяется в{" "}
                      <AnchorLink href="/system/manage/service_tools">
                        Справочнике Service Tools
                      </AnchorLink>
                      .
                    </Typography>
                  </Box>
                }
              >
                <WarningAmberOutlinedIcon
                  sx={{ color: "warning.main" }}
                  fontSize="small"
                />
              </Tooltip>
            ) : (
              <Checkbox
                size="small"
                checked={!!row[key]}
                disabled={disabled || readOnly}
                onChange={(_, checked) =>
                  toggleAccountCell(row.id, key, checked)
                }
                inputProps={{ "aria-label": `${label} checkbox` }}
              />
            )}
          </Box>
        );
      },
    }));

    return [...base, ...toolCols];
  }, [rows, toggleAccountCell, readOnly]);

  if (
    appLvlSettingsLoading ||
    serviceLvlSettingsLoading ||
    accountLvlSettingsLoading ||
    checkLaunchedProcessesLoading
  ) {
    return <Loader position="absolute" />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <Box sx={{ display: "flex", gap: 3 }}>
        <Typography variant="h6">
          Управление приложениями Service Tools
        </Typography>

        {showActions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 3,
              ml: "auto",
            }}
          >
            <Button variant="outlined" onClick={handleCancel}>
              Отмена
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                readOnly ||
                isUpdateAppLvlSettings ||
                isUpdateServiceLvlSettings ||
                isUpdateAccountLvlSettings
              }
            >
              Сохранить
            </Button>
          </Box>
        )}
      </Box>

      {isLaunched ? (
        <Box
          sx={{
            mt: 2,
            zIndex: (t) => t.zIndex.modal + 1,
          }}
        >
          <Alert variant="filled" severity="warning">
            Обновляем данные, чтобы всё отображалось корректно. Вернитесь через
            пару минут и перезагрузите страницу.
          </Alert>
        </Box>
      ) : (
        <>
          <Paper
            variant="outlined"
            elevation={0}
            sx={{ p: 3, borderRadius: 2, mt: 3 }}
          >
            <Grid
              container
              columns={{ xs: 12 }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  По приложениям
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <SwitchRow<string, { id: string; label: string }>
                    items={appItems}
                    getKey={(i) => i.id}
                    getLabel={(i) => i.label}
                    state={appsState}
                    onChange={handleToggleApp}
                    lockedMap={appLockedMap}
                    disabledMap={appDisabledMapWithPermissions}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            variant="outlined"
            elevation={0}
            sx={{ p: 3, borderRadius: 2, mt: 3 }}
          >
            <Grid
              container
              columns={{ xs: 12 }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  По сервисам
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <SwitchRow<string, { id: string; label: string }>
                    items={serviceItems}
                    getKey={(i) => i.id}
                    getLabel={(i) => i.label}
                    state={svcState}
                    hideLabels
                    onChange={handleToggleService}
                    lockedMap={serviceLockedMap}
                    disabledMap={serviceDisabledMapWithPermissions}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              mt: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Grid
              container
              columns={{ xs: 12 }}
              spacing={2}
              sx={{ flex: 1, minHeight: 0 }}
            >
              <Grid
                size={{ xs: 12 }}
                sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  По аккаунтам
                </Typography>
                <TablePage<Row>
                  columnHeaderHeight={80}
                  tabsOptions={serviceTabsOptions}
                  tabsValue={currentService}
                  handleTabsChange={handleGuardedTabsChange}
                  columns={columns}
                  rows={rows}
                  loading={accountLvlSettingsLoading}
                  paginationMode="server"
                  rowCount={accountLvlSettingsData?.meta?.total ?? 0}
                  paginationModel={{ page: page - 1, pageSize: per_page }}
                  onPaginationModelChange={handleGuardedPaginationChange}
                  pageSizeOptions={[15, 25, 50, 100]}
                  disableRowSelectionOnClick
                  getRowId={(r) => r.id}
                  localeText={{
                    noRowsLabel: "Нет аккаунтов для выбранного сервиса",
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {crossGuardModal}
        </>
      )}
    </Box>
  );
};
